import React from "react";
import {
  Gem,
  Handshake,
  Scale,
  HardHat,
  TrendingUp,
} from "lucide-react";
import { cn } from "../../lib/utils";
import { SAMPLE_ARTICLES, CONTENT_CATEGORIES } from "../../lib/constants";
import { SectionHeader } from "../ui/Divider";
import { Card, CardContent } from "../ui/Card";

const iconMap = {
  Gem,
  Handshake,
  Scale,
  HardHat,
  TrendingUp,
};

export function ContentShowcase({ className }) {
  const featuredArticle = SAMPLE_ARTICLES.find((a) => a.featured);
  const otherArticles = SAMPLE_ARTICLES.filter((a) => !a.featured).slice(0, 4);

  return (
    <section
      className={cn(
        "py-12 md:py-16 px-4",
        "bg-paper",
        className
      )}
    >
      <div className="max-w-6xl mx-auto">
        <SectionHeader
          title="Sample Coverage"
          subtitle="A sample of the intelligence you'll receive in each edition of The Arabian-Nubian Brief."
        />

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Featured Article - Large */}
          {featuredArticle && (
            <Card
              variant="bordered"
              className={cn(
                "lg:col-span-7 lg:row-span-2",
                "card-lift scroll-animate",
                "group cursor-pointer"
              )}
            >
              <CardContent className="p-6 md:p-8 h-full flex flex-col">
                {/* Category Badge */}
                <div className="flex items-center gap-2 mb-4">
                  <Gem className="w-4 h-4 text-gold" />
                  <span className="font-sans text-xs uppercase tracking-wider text-gold font-semibold">
                    Featured Story
                  </span>
                </div>

                {/* Title */}
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-ink mb-4 group-hover:text-gold transition-colors">
                  {featuredArticle.title}
                </h3>

                {/* Excerpt */}
                <p className="font-body text-ink-secondary leading-relaxed flex-grow">
                  {featuredArticle.excerpt}
                </p>

                {/* Footer */}
                <div className="flex items-center mt-6 pt-4 border-t border-border">
                  <span className="font-sans text-sm text-ink-muted">
                    {featuredArticle.date}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Other Articles Grid */}
          {otherArticles.map((article, index) => {
            const category = CONTENT_CATEGORIES.find(
              (c) => c.id === article.category
            );
            const Icon = category ? iconMap[category.icon] : null;

            return (
              <Card
                key={article.id}
                variant="elevated"
                className={cn(
                  "lg:col-span-5",
                  "card-lift scroll-animate",
                  "group cursor-pointer"
                )}
                style={{ transitionDelay: `${(index + 1) * 0.1}s` }}
              >
                <CardContent className="p-5">
                  {/* Category */}
                  <div className="flex items-center gap-2 mb-3">
                    {Icon && (
                      <Icon className="w-4 h-4 text-ink-muted group-hover:text-gold transition-colors" />
                    )}
                    <span className="font-sans text-xs uppercase tracking-wider text-ink-muted">
                      {category?.name || article.category}
                    </span>
                  </div>

                  {/* Title */}
                  <h4 className="font-serif text-lg font-semibold text-ink mb-2 group-hover:text-gold transition-colors line-clamp-2">
                    {article.title}
                  </h4>

                  {/* Date */}
                  <span className="font-sans text-xs text-ink-light">
                    {article.date}
                  </span>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Category Tags */}
        <div className="mt-12 text-center">
          <p className="font-sans text-sm text-ink-muted mb-4">
            Coverage includes:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {CONTENT_CATEGORIES.map((category) => {
              const Icon = iconMap[category.icon];
              return (
                <div
                  key={category.id}
                  className={cn(
                    "flex items-center gap-2",
                    "px-4 py-2",
                    "bg-paper-dark/50 rounded-full",
                    "border border-border",
                    "font-sans text-sm text-ink-secondary"
                  )}
                >
                  {Icon && <Icon className="w-4 h-4 text-gold" />}
                  {category.name}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
